import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import AvisoNovaVersao from './components/AvisoNovaVersao';
import BarraAbas from './components/BarraAbas';
import Login from './pages/Login';
import Atendimentos from './pages/Atendimentos';
import Painel from './pages/Painel';
import Usuarios from './pages/Usuarios';
import Perfis from './pages/Perfis';
import AdminWhatsapp from './pages/AdminWhatsapp';
import AdminAtendimento from './pages/AdminAtendimento';
import Indicadores from './pages/Indicadores';
import Contatos from './pages/Contatos';
import Grupos from './pages/Grupos';
import Configuracoes from './pages/Configuracoes';
import Mais from './pages/Mais';
import AdminFluxos from './pages/AdminFluxos';
import { avisarTema } from './helpers/appNativo';
import { usePrefs } from './helpers/prefs';

// Ponte com o app do Fio. Sem o app instalado nada disto acontece.
//
// Duas coisas que só o Fio sabe e só a casca pode executar:
//
// 1. NAVEGAR. Quando a pessoa toca no aviso ("Cliente esperando"), quem sabe
//    pra onde ir é a casca — mas quem sabe TROCAR DE TELA é o React Router,
//    aqui dentro. Expor a função é o que permite abrir a conversa sem
//    recarregar: recarregar cairia na tela inicial, perderia o SSE e faria a
//    conversa demorar justo no momento em que tem cliente esperando.
//
// 2. O TEMA. A moldura do app (área de status, fundo, tela de sem-conexão) é
//    pintada pelo lado nativo, que não enxerga o CSS daqui. Sem avisar, o Fio
//    no tema claro aparece dentro de moldura escura.
function PonteDoApp() {
  const navigate = useNavigate();
  const prefs = usePrefs();

  useEffect(() => {
    window.FioNavegar = (url) => {
      try { navigate(String(url || '/')); } catch (e) { window.location.href = url; }
    };
    return () => { delete window.FioNavegar; };
  }, [navigate]);

  useEffect(() => { avisarTema(prefs.tema); }, [prefs.tema]);

  return null;
}

function FullScreenLoader() {
  return (
    <div className="screen-loader">
      <div className="screen-loader__spinner" />
      <p>Carregando Fio...</p>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppLayout({ children }) {
  return (
    <div className="layout">
      {/* Flutua por cima (position: fixed) de propósito: na conversa em tela
          cheia do tablet a altura é travada, e uma faixa em fluxo empurraria
          a barra de digitar pra fora da tela. */}
      <AvisoNovaVersao />
      <Sidebar />
      <main className="main-content">{children}</main>
      {/* Só aparece no dedo (CSS). No mouse, quem navega é a sidebar. */}
      <BarraAbas />
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/ti/atendimentos" replace /> : <Login />} />

      {/* Atendimento */}
      <Route
        path="/ti/atendimentos"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Atendimentos />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/ti/painel"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Painel />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/ti/atendimentos/indicadores"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Indicadores />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/ti/contatos"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Contatos />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/ti/configuracoes"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Configuracoes />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/ti/grupos"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Grupos />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/ti/mais"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Mais />
            </AppLayout>
          </PrivateRoute>
        )}
      />

      {/* Admin */}
      <Route
        path="/admin/usuarios"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Usuarios />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/admin/perfis"
        element={(
          <PrivateRoute>
            <AppLayout>
              <Perfis />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/admin/whatsapp"
        element={(
          <PrivateRoute>
            <AppLayout>
              <AdminWhatsapp />
            </AppLayout>
          </PrivateRoute>
        )}
      />
      <Route
        path="/admin/atendimento"
        element={(
          <PrivateRoute>
            <AppLayout>
              <AdminAtendimento />
            </AppLayout>
          </PrivateRoute>
        )}
      />

      {/* Flow Builder - editor de fluxos do bot (RFC-001) */}
      <Route
        path="/admin/fluxos"
        element={(
          <PrivateRoute>
            <AppLayout>
              <AdminFluxos />
            </AppLayout>
          </PrivateRoute>
        )}
      />

      <Route path="*" element={<Navigate to={user ? '/ti/atendimentos' : '/login'} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PonteDoApp />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
